import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { adminOrderLifecycleService } from '../../../services/orderLifecycleService';

const OrderLifecycleTab = ({ language }) => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionData, setActionData] = useState({});
  const [statistics, setStatistics] = useState(null);

  const content = {
    en: {
      title: 'Order Lifecycle Management',
      search: 'Search orders...',
      allStatus: 'All Status',
      allPriority: 'All Priority',
      noOrders: 'No orders found',
      orderId: 'Order ID',
      buyer: 'Buyer',
      bean: 'Coffee Bean',
      quantity: 'Quantity',
      status: 'Status',
      priority: 'Priority',
      created: 'Created',
      actions: 'Actions',
      viewDetails: 'View Details',
      confirmOrder: 'Confirm Order',
      verifyPayment: 'Verify Payment',
      scheduleShipment: 'Schedule Shipment',
      updateTracking: 'Update Tracking',
      confirmDelivery: 'Confirm Delivery',
      openDispute: 'Open Dispute',
      resolveDispute: 'Resolve Dispute',
      timeline: 'Timeline',
      documents: 'Documents',
      orderDetails: 'Order Details',
      buyerInfo: 'Buyer Information',
      paymentInfo: 'Payment Information',
      shipmentInfo: 'Shipment Information',
      disputeInfo: 'Dispute Information',
      close: 'Close',
      submit: 'Submit',
      cancel: 'Cancel',
      notes: 'Notes',
      paymentMethod: 'Payment Method',
      paymentReference: 'Payment Reference',
      paymentAmount: 'Payment Amount (USD)',
      shipmentDate: 'Shipment Date',
      carrier: 'Carrier',
      trackingNumber: 'Tracking Number',
      estimatedDelivery: 'Estimated Delivery',
      disputeReason: 'Dispute Reason',
      resolution: 'Resolution',
      statistics: 'Statistics',
      totalOrders: 'Total Orders',
      export: 'Export'
    },
    zh: {
      title: '订单生命周期管理',
      search: '搜索订单...',
      allStatus: '所有状态',
      allPriority: '所有优先级',
      noOrders: '未找到订单',
      orderId: '订单号',
      buyer: '买家',
      bean: '咖啡豆',
      quantity: '数量',
      status: '状态',
      priority: '优先级',
      created: '创建时间',
      actions: '操作',
      viewDetails: '查看详情',
      confirmOrder: '确认订单',
      verifyPayment: '验证付款',
      scheduleShipment: '安排发货',
      updateTracking: '更新跟踪',
      confirmDelivery: '确认交付',
      openDispute: '开启争议',
      resolveDispute: '解决争议',
      timeline: '时间线',
      documents: '文档',
      orderDetails: '订单详情',
      buyerInfo: '买家信息',
      paymentInfo: '付款信息',
      shipmentInfo: '发货信息',
      disputeInfo: '争议信息',
      close: '关闭',
      submit: '提交',
      cancel: '取消',
      notes: '备注',
      paymentMethod: '付款方式',
      paymentReference: '付款参考',
      paymentAmount: '付款金额 (USD)',
      shipmentDate: '发货日期',
      carrier: '承运商',
      trackingNumber: '跟踪号',
      estimatedDelivery: '预计交付',
      disputeReason: '争议原因',
      resolution: '解决方案',
      statistics: '统计',
      totalOrders: '总订单数',
      export: '导出'
    }
  };

  const t = content?.[language];

  const statusOptions = [
    { value: 'all', label: t?.allStatus },
    { value: 'pending', label: language === 'en' ? 'Pending' : '待处理' },
    { value: 'confirmed', label: language === 'en' ? 'Confirmed' : '已确认' },
    { value: 'payment_pending', label: language === 'en' ? 'Payment Pending' : '待付款' },
    { value: 'payment_verified', label: language === 'en' ? 'Payment Verified' : '付款已验证' },
    { value: 'payment_failed', label: language === 'en' ? 'Payment Failed' : '付款失败' },
    { value: 'shipment_scheduled', label: language === 'en' ? 'Shipment Scheduled' : '已安排发货' },
    { value: 'in_transit', label: language === 'en' ? 'In Transit' : '运输中' },
    { value: 'delivered', label: language === 'en' ? 'Delivered' : '已交付' },
    { value: 'disputed', label: language === 'en' ? 'Disputed' : '争议中' },
    { value: 'resolved', label: language === 'en' ? 'Resolved' : '已解决' },
    { value: 'cancelled', label: language === 'en' ? 'Cancelled' : '已取消' }
  ];

  const priorityOptions = [
    { value: 'all', label: t?.allPriority },
    { value: 'low', label: language === 'en' ? 'Low' : '低' },
    { value: 'normal', label: language === 'en' ? 'Normal' : '正常' },
    { value: 'high', label: language === 'en' ? 'High' : '高' },
    { value: 'urgent', label: language === 'en' ? 'Urgent' : '紧急' }
  ];

  useEffect(() => {
    loadOrders();
    loadStatistics();
  }, [filterStatus, filterPriority]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filterStatus !== 'all') filters.status = filterStatus;
      if (filterPriority !== 'all') filters.priority = filterPriority;
      const data = await adminOrderLifecycleService?.getAll(filters);
      setOrders(data);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await adminOrderLifecycleService?.getStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const loadOrderDetails = async (orderId) => {
    try {
      const [orderData, timelineData, documentsData] = await Promise.all([
        adminOrderLifecycleService?.getById(orderId),
        adminOrderLifecycleService?.getTimelineEvents(orderId),
        adminOrderLifecycleService?.getDocuments(orderId)
      ]);
      setSelectedOrder(orderData);
      setTimelineEvents(timelineData);
      setDocuments(documentsData);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load order details:', error);
    }
  };

  const handleAction = async () => {
    try {
      switch (actionType) {
        case 'confirm':
          await adminOrderLifecycleService?.confirmOrder(selectedOrder?.id, actionData?.notes);
          break;
        case 'payment':
          await adminOrderLifecycleService?.updatePaymentStatus(selectedOrder?.id, {
            status: actionData?.status,
            paymentMethod: actionData?.paymentMethod,
            paymentReference: actionData?.paymentReference,
            paymentAmountUsd: actionData?.paymentAmount,
            paymentNotes: actionData?.notes
          });
          break;
        case 'shipment':
          await adminOrderLifecycleService?.scheduleShipment(selectedOrder?.id, {
            shipmentScheduledDate: actionData?.shipmentDate,
            shipmentMethod: actionData?.shipmentMethod,
            trackingNumber: actionData?.trackingNumber,
            carrier: actionData?.carrier,
            estimatedDeliveryDate: actionData?.estimatedDelivery
          });
          break;
        case 'tracking':
          await adminOrderLifecycleService?.updateShipmentStatus(
            selectedOrder?.id,
            actionData?.status,
            actionData?.notes
          );
          break;
        case 'delivery':
          await adminOrderLifecycleService?.updateShipmentStatus(
            selectedOrder?.id,
            'delivered',
            actionData?.notes
          );
          break;
        case 'dispute':
          await adminOrderLifecycleService?.openDispute(selectedOrder?.id, actionData?.reason);
          break;
        case 'resolve':
          await adminOrderLifecycleService?.resolveDispute(selectedOrder?.id, actionData?.resolution);
          break;
        default:
          break;
      }
      setShowActionModal(false);
      setActionData({});
      await loadOrders();
      if (selectedOrder) {
        await loadOrderDetails(selectedOrder?.id);
      }
    } catch (error) {
      alert(language === 'en' ? `Action failed: ${error?.message}` : `操作失败: ${error?.message}`);
    }
  };

  const openActionModal = (type, order) => {
    setSelectedOrder(order);
    setActionType(type);
    setActionData({});
    setShowActionModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning/10 text-warning',
      confirmed: 'bg-primary/10 text-primary',
      payment_pending: 'bg-warning/10 text-warning',
      payment_verified: 'bg-success/10 text-success',
      payment_failed: 'bg-destructive/10 text-destructive',
      shipment_scheduled: 'bg-primary/10 text-primary',
      in_transit: 'bg-primary/10 text-primary',
      delivered: 'bg-success/10 text-success',
      disputed: 'bg-destructive/10 text-destructive',
      resolved: 'bg-success/10 text-success',
      cancelled: 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-muted text-muted-foreground',
      normal: 'bg-primary/10 text-primary',
      high: 'bg-warning/10 text-warning',
      urgent: 'bg-destructive/10 text-destructive'
    };
    return colors?.[priority] || colors?.normal;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'Clock',
      confirmed: 'CheckCircle2',
      payment_pending: 'CreditCard',
      payment_verified: 'CheckCircle2',
      payment_failed: 'XCircle',
      shipment_scheduled: 'Package',
      in_transit: 'Truck',
      delivered: 'CheckCircle2',
      disputed: 'AlertCircle',
      resolved: 'CheckCircle2',
      cancelled: 'XCircle'
    };
    return icons?.[status] || 'Circle';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(new Date(date));
  };

  const filteredOrders = orders?.filter(order => {
    const matchesSearch = 
      order?.buyer?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      order?.buyer?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      order?.bean?.nameEn?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      order?.bean?.nameZh?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      order?.id?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesSearch;
  });

  const getAvailableActions = (order) => {
    const actions = [];
    
    if (order?.status === 'pending') {
      actions?.push({ type: 'confirm', label: t?.confirmOrder, icon: 'CheckCircle2', variant: 'default' });
    }
    
    if (['confirmed', 'payment_pending']?.includes(order?.status)) {
      actions?.push({ type: 'payment', label: t?.verifyPayment, icon: 'CreditCard', variant: 'default' });
    }
    
    if (order?.status === 'payment_verified') {
      actions?.push({ type: 'shipment', label: t?.scheduleShipment, icon: 'Package', variant: 'default' });
    }
    
    if (order?.status === 'shipment_scheduled') {
      actions?.push({ type: 'tracking', label: t?.updateTracking, icon: 'Truck', variant: 'default' });
    }
    
    if (order?.status === 'in_transit') {
      actions?.push({ type: 'delivery', label: t?.confirmDelivery, icon: 'CheckCircle2', variant: 'success' });
    }
    
    if (!['disputed', 'resolved', 'cancelled']?.includes(order?.status)) {
      actions?.push({ type: 'dispute', label: t?.openDispute, icon: 'AlertCircle', variant: 'destructive' });
    }
    
    if (order?.status === 'disputed') {
      actions?.push({ type: 'resolve', label: t?.resolveDispute, icon: 'CheckCircle2', variant: 'success' });
    }
    
    return actions;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Statistics */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-1">{t?.totalOrders}</p>
            <p className="text-2xl font-bold text-foreground">{statistics?.total}</p>
          </div>
          {Object.entries(statistics?.byStatus || {})?.slice(0, 3)?.map(([status, count]) => (
            <div key={status} className="bg-card border border-border rounded-lg p-4">
              <p className="text-sm text-muted-foreground mb-1 capitalize">{status?.replace('_', ' ')}</p>
              <p className="text-2xl font-bold text-foreground">{count}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Input
            type="search"
            placeholder={t?.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full sm:w-64"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {statusOptions?.map(option => (
              <option key={option?.value} value={option?.value}>{option?.label}</option>
            ))}
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {priorityOptions?.map(option => (
              <option key={option?.value} value={option?.value}>{option?.label}</option>
            ))}
          </select>
        </div>
        <Button variant="outline" iconName="Download" iconPosition="left">
          {t?.export}
        </Button>
      </div>

      {/* Orders List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredOrders?.map((order) => (
          <div key={order?.id} className="bg-card border border-border rounded-lg p-5 hover:shadow-md transition-smooth">
            <div className="flex flex-col lg:flex-row lg:items-start gap-4">
              <div className="flex-1 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-semibold text-foreground">{order?.buyer?.fullName}</h3>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(order?.priority)}`}>
                        {order?.priority?.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">{formatDate(order?.createdAt)}</p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium ${getStatusColor(order?.status)}`}>
                    <Icon name={getStatusIcon(order?.status)} size={14} />
                    {statusOptions?.find(s => s?.value === order?.status)?.label}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
                      <span className="text-muted-foreground truncate">{order?.buyer?.email}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Coffee" size={16} color="var(--color-primary)" />
                      <span className="text-foreground font-medium">
                        {language === 'en' ? order?.bean?.nameEn : order?.bean?.nameZh}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Icon name="Package" size={16} color="var(--color-muted-foreground)" />
                      <span className="text-muted-foreground">{order?.quantityKg} kg</span>
                    </div>
                    {order?.trackingNumber && (
                      <div className="flex items-center gap-2 text-sm">
                        <Icon name="Truck" size={16} color="var(--color-primary)" />
                        <span className="text-foreground font-mono text-xs">{order?.trackingNumber}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex lg:flex-col gap-2 lg:w-40">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Eye"
                  iconPosition="left"
                  onClick={() => loadOrderDetails(order?.id)}
                  fullWidth
                >
                  {t?.viewDetails}
                </Button>
                {getAvailableActions(order)?.slice(0, 1)?.map((action) => (
                  <Button
                    key={action?.type}
                    variant={action?.variant}
                    size="sm"
                    iconName={action?.icon}
                    iconPosition="left"
                    onClick={() => openActionModal(action?.type, order)}
                    fullWidth
                  >
                    {action?.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredOrders?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
          <p className="text-muted-foreground">{t?.noOrders}</p>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-heading font-bold text-foreground">{t?.orderDetails}</h2>
              <button onClick={() => setShowDetailModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Order Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t?.orderId}</p>
                  <p className="font-mono text-sm">{selectedOrder?.id}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t?.status}</p>
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium ${getStatusColor(selectedOrder?.status)}`}>
                    <Icon name={getStatusIcon(selectedOrder?.status)} size={14} />
                    {statusOptions?.find(s => s?.value === selectedOrder?.status)?.label}
                  </span>
                </div>
              </div>

              {/* Buyer Info */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">{t?.buyerInfo}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-muted/30 rounded-lg p-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="text-sm font-medium">{selectedOrder?.buyer?.fullName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="text-sm font-medium">{selectedOrder?.buyer?.email}</p>
                  </div>
                  {selectedOrder?.buyer?.phone && (
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <p className="text-sm font-medium">{selectedOrder?.buyer?.phone}</p>
                    </div>
                  )}
                  {selectedOrder?.buyer?.companyName && (
                    <div>
                      <p className="text-sm text-muted-foreground">Company</p>
                      <p className="text-sm font-medium">{selectedOrder?.buyer?.companyName}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Info */}
              {selectedOrder?.paymentMethod && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">{t?.paymentInfo}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-muted/30 rounded-lg p-4">
                    <div>
                      <p className="text-sm text-muted-foreground">{t?.paymentMethod}</p>
                      <p className="text-sm font-medium">{selectedOrder?.paymentMethod}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t?.paymentAmount}</p>
                      <p className="text-sm font-medium">${selectedOrder?.paymentAmountUsd}</p>
                    </div>
                    {selectedOrder?.paymentReference && (
                      <div>
                        <p className="text-sm text-muted-foreground">{t?.paymentReference}</p>
                        <p className="text-sm font-medium font-mono">{selectedOrder?.paymentReference}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Shipment Info */}
              {selectedOrder?.trackingNumber && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">{t?.shipmentInfo}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-muted/30 rounded-lg p-4">
                    <div>
                      <p className="text-sm text-muted-foreground">{t?.carrier}</p>
                      <p className="text-sm font-medium">{selectedOrder?.carrier}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t?.trackingNumber}</p>
                      <p className="text-sm font-medium font-mono">{selectedOrder?.trackingNumber}</p>
                    </div>
                    {selectedOrder?.estimatedDeliveryDate && (
                      <div>
                        <p className="text-sm text-muted-foreground">{t?.estimatedDelivery}</p>
                        <p className="text-sm font-medium">{formatDate(selectedOrder?.estimatedDeliveryDate)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">{t?.timeline}</h3>
                <div className="space-y-3">
                  {timelineEvents?.map((event, index) => (
                    <div key={event?.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Icon name="Circle" size={12} color="var(--color-primary)" />
                        </div>
                        {index < timelineEvents?.length - 1 && (
                          <div className="w-0.5 h-full bg-border mt-1" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <p className="font-medium text-foreground">{event?.eventTitle}</p>
                        {event?.eventDescription && (
                          <p className="text-sm text-muted-foreground mt-1">{event?.eventDescription}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">{formatDate(event?.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Documents */}
              {documents?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">{t?.documents}</h3>
                  <div className="space-y-2">
                    {documents?.map((doc) => (
                      <div key={doc?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Icon name="FileText" size={20} color="var(--color-primary)" />
                          <div>
                            <p className="text-sm font-medium">{doc?.documentName}</p>
                            <p className="text-xs text-muted-foreground">{doc?.documentType}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" iconName="Download" />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 flex-wrap">
                {getAvailableActions(selectedOrder)?.map((action) => (
                  <Button
                    key={action?.type}
                    variant={action?.variant}
                    size="sm"
                    iconName={action?.icon}
                    iconPosition="left"
                    onClick={() => {
                      setShowDetailModal(false);
                      openActionModal(action?.type, selectedOrder);
                    }}
                  >
                    {action?.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full">
            <div className="border-b border-border p-4">
              <h2 className="text-xl font-heading font-bold text-foreground">
                {actionType === 'confirm' && t?.confirmOrder}
                {actionType === 'payment' && t?.verifyPayment}
                {actionType === 'shipment' && t?.scheduleShipment}
                {actionType === 'tracking' && t?.updateTracking}
                {actionType === 'delivery' && t?.confirmDelivery}
                {actionType === 'dispute' && t?.openDispute}
                {actionType === 'resolve' && t?.resolveDispute}
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              {actionType === 'confirm' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t?.notes}</label>
                  <textarea
                    value={actionData?.notes || ''}
                    onChange={(e) => setActionData({ ...actionData, notes: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={3}
                  />
                </div>
              )}

              {actionType === 'payment' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                    <select
                      value={actionData?.status || 'payment_verified'}
                      onChange={(e) => setActionData({ ...actionData, status: e?.target?.value })}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="payment_verified">Payment Verified</option>
                      <option value="payment_failed">Payment Failed</option>
                      <option value="payment_pending">Payment Pending</option>
                    </select>
                  </div>
                  <Input
                    label={t?.paymentMethod}
                    value={actionData?.paymentMethod || ''}
                    onChange={(e) => setActionData({ ...actionData, paymentMethod: e?.target?.value })}
                  />
                  <Input
                    label={t?.paymentReference}
                    value={actionData?.paymentReference || ''}
                    onChange={(e) => setActionData({ ...actionData, paymentReference: e?.target?.value })}
                  />
                  <Input
                    label={t?.paymentAmount}
                    type="number"
                    value={actionData?.paymentAmount || ''}
                    onChange={(e) => setActionData({ ...actionData, paymentAmount: e?.target?.value })}
                  />
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{t?.notes}</label>
                    <textarea
                      value={actionData?.notes || ''}
                      onChange={(e) => setActionData({ ...actionData, notes: e?.target?.value })}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={2}
                    />
                  </div>
                </>
              )}

              {actionType === 'shipment' && (
                <>
                  <Input
                    label={t?.shipmentDate}
                    type="date"
                    value={actionData?.shipmentDate || ''}
                    onChange={(e) => setActionData({ ...actionData, shipmentDate: e?.target?.value })}
                  />
                  <Input
                    label={t?.carrier}
                    value={actionData?.carrier || ''}
                    onChange={(e) => setActionData({ ...actionData, carrier: e?.target?.value })}
                  />
                  <Input
                    label={t?.trackingNumber}
                    value={actionData?.trackingNumber || ''}
                    onChange={(e) => setActionData({ ...actionData, trackingNumber: e?.target?.value })}
                  />
                  <Input
                    label={t?.estimatedDelivery}
                    type="date"
                    value={actionData?.estimatedDelivery || ''}
                    onChange={(e) => setActionData({ ...actionData, estimatedDelivery: e?.target?.value })}
                  />
                </>
              )}

              {actionType === 'tracking' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Status</label>
                    <select
                      value={actionData?.status || 'in_transit'}
                      onChange={(e) => setActionData({ ...actionData, status: e?.target?.value })}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="in_transit">In Transit</option>
                      <option value="delivered">Delivered</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">{t?.notes}</label>
                    <textarea
                      value={actionData?.notes || ''}
                      onChange={(e) => setActionData({ ...actionData, notes: e?.target?.value })}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      rows={2}
                    />
                  </div>
                </>
              )}

              {actionType === 'delivery' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t?.notes}</label>
                  <textarea
                    value={actionData?.notes || ''}
                    onChange={(e) => setActionData({ ...actionData, notes: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={3}
                    placeholder="Delivery confirmation notes..."
                  />
                </div>
              )}

              {actionType === 'dispute' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t?.disputeReason}</label>
                  <textarea
                    value={actionData?.reason || ''}
                    onChange={(e) => setActionData({ ...actionData, reason: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={4}
                    placeholder="Describe the dispute..."
                  />
                </div>
              )}

              {actionType === 'resolve' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">{t?.resolution}</label>
                  <textarea
                    value={actionData?.resolution || ''}
                    onChange={(e) => setActionData({ ...actionData, resolution: e?.target?.value })}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={4}
                    placeholder="Describe the resolution..."
                  />
                </div>
              )}
            </div>

            <div className="border-t border-border p-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowActionModal(false)}>
                {t?.cancel}
              </Button>
              <Button variant="default" onClick={handleAction}>
                {t?.submit}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderLifecycleTab;